import type { ChurnIndicators } from "@/types/analytics";

export interface ChurnScoreFactors {
  recencyScore: number;
  frequencyScore: number;
  monetaryScore: number;
  engagementScore: number;
  conversionScore: number;
  sessionHealthScore: number;
}

interface ChurnThresholds {
  highRiskThreshold: number;
  mediumRiskThreshold: number;
  inactiveThreshold: number;
}

const DEFAULT_THRESHOLDS: ChurnThresholds = {
  highRiskThreshold: 0.65,
  mediumRiskThreshold: 0.45,
  inactiveThreshold: 60,
};

function calculateRecencyScore(daysSinceLastVisit: number): number {
  if (daysSinceLastVisit <= 7) return 100;
  if (daysSinceLastVisit <= 30) return 100 - (daysSinceLastVisit - 7) * 2;
  if (daysSinceLastVisit <= 90) return 60 - (daysSinceLastVisit - 30);
  return Math.max(0, 30 - Math.floor((daysSinceLastVisit - 90) / 10));
}

function calculateFrequencyScore(totalSessions: number): number {
  if (totalSessions >= 20) return 100;
  if (totalSessions >= 10) return 70 + totalSessions;
  if (totalSessions >= 5) return 50 + totalSessions * 2;
  if (totalSessions >= 1) return Math.max(20, totalSessions * 10);
  return 0;
}

function calculateMonetaryScore(formCompletionRate: number, totalFormAttempts: number): number {
  const successfulSubmissions = Math.round(totalFormAttempts * formCompletionRate);

  if (successfulSubmissions >= 5) return 100;
  if (successfulSubmissions >= 3) return 80 + successfulSubmissions * 5;
  if (successfulSubmissions >= 1) return 50 + successfulSubmissions * 15;
  return 0;
}

function calculateEngagementScore(totalPageViews: number, averageSessionDuration: number): number {
  const viewScore = Math.min(100, (totalPageViews / 30) * 100);
  const durationScore = Math.min(100, (averageSessionDuration / 300) * 100);

  return (viewScore + durationScore) / 2;
}

function calculateConversionScore(formCompletionRate: number): number {
  return formCompletionRate * 100;
}

function calculateSessionHealthScore(totalSessions: number, averageSessionDuration: number): number {
  const sessionHealthy = totalSessions >= 5;
  const durationHealthy = averageSessionDuration >= 60;

  let score = 0;
  if (sessionHealthy) score += 50;
  if (durationHealthy) score += 50;

  return score;
}

function calculateChurnScore(factors: ChurnScoreFactors): number {
  const weights = {
    recency: 0.35,
    frequency: 0.2,
    monetary: 0.15,
    engagement: 0.15,
    conversion: 0.1,
    sessionHealth: 0.05,
  };

  return (
    factors.recencyScore * weights.recency +
    factors.frequencyScore * weights.frequency +
    factors.monetaryScore * weights.monetary +
    factors.engagementScore * weights.engagement +
    factors.conversionScore * weights.conversion +
    factors.sessionHealthScore * weights.sessionHealth
  );
}

function determineChurnProbability(churnScore: number, thresholds: ChurnThresholds): "high" | "medium" | "low" {
  if (churnScore >= thresholds.highRiskThreshold * 100) return "low";
  if (churnScore >= thresholds.mediumRiskThreshold * 100) return "medium";
  return "high";
}

function identifyChurnReasons(indicators: ChurnIndicators, factors: ChurnScoreFactors): string[] {
  const reasons: string[] = [];

  if (factors.recencyScore < 30) {
    reasons.push("inactive_for_extended_period");
  }

  if (factors.frequencyScore < 30) {
    reasons.push("low_session_frequency");
  }

  if (factors.conversionScore < 30) {
    reasons.push("low_form_completion");
  }

  if (indicators.totalFormAttempts > 0 && indicators.formCompletionRate < 0.3) {
    reasons.push("high_abandonment_rate");
  }

  if (factors.engagementScore < 30) {
    reasons.push("low_engagement");
  }

  if (indicators.averageSessionDuration < 30) {
    reasons.push("short_session_duration");
  }

  if (indicators.daysSinceLastFormSubmission > 90 && indicators.totalFormAttempts === 0) {
    reasons.push("never_submitted_form");
  }

  return reasons.length > 0 ? reasons : ["insufficient_engagement"];
}

export function calculateChurnIndicators(
  indicators: Partial<ChurnIndicators>,
  thresholds: Partial<ChurnThresholds> = {},
): ChurnIndicators {
  const mergedThresholds = { ...DEFAULT_THRESHOLDS, ...thresholds };

  const daysSinceLastVisit = indicators.daysSinceLastVisit ?? 365;
  const totalSessions = indicators.totalSessions ?? 0;
  const formCompletionRate = indicators.formCompletionRate ?? 0;
  const totalPageViews = indicators.totalPageViews ?? 0;
  const averageSessionDuration = indicators.averageSessionDuration ?? 0;
  const totalFormAttempts = indicators.totalFormAttempts ?? 0;
  const daysSinceLastFormSubmission = indicators.daysSinceLastFormSubmission ?? 365;

  const factors: ChurnScoreFactors = {
    recencyScore: calculateRecencyScore(daysSinceLastVisit),
    frequencyScore: calculateFrequencyScore(totalSessions),
    monetaryScore: calculateMonetaryScore(formCompletionRate, totalFormAttempts),
    engagementScore: calculateEngagementScore(totalPageViews, averageSessionDuration),
    conversionScore: calculateConversionScore(formCompletionRate),
    sessionHealthScore: calculateSessionHealthScore(totalSessions, averageSessionDuration),
  };

  const churnScore = calculateChurnScore(factors);
  const churnProbability = determineChurnProbability(churnScore, mergedThresholds);
  const churnReason = identifyChurnReasons(indicators as ChurnIndicators, factors).join(", ");

  return {
    daysSinceLastVisit,
    daysSinceLastFormSubmission,
    totalPageViews,
    totalFormAttempts,
    formCompletionRate,
    averageSessionDuration,
    totalSessions,
    hasCompletedForm: formCompletionRate > 0 && totalFormAttempts > 0,
    hasVisitedMultiplePages: totalPageViews > 1,
    hasShownInterest: totalPageViews > 0 || totalFormAttempts > 0 || totalSessions > 0,
    churnProbability,
    churnReason: churnReason || undefined,
  };
}

export function isUserChurned(indicators: ChurnIndicators): boolean {
  return indicators.churnProbability === "high";
}

export function isUserAtRisk(indicators: ChurnIndicators): boolean {
  return indicators.churnProbability === "high" || indicators.churnProbability === "medium";
}

export function getChurnFactors(indicators: ChurnIndicators): ChurnScoreFactors {
  return {
    recencyScore: calculateRecencyScore(indicators.daysSinceLastVisit),
    frequencyScore: calculateFrequencyScore(indicators.totalSessions),
    monetaryScore: calculateMonetaryScore(indicators.formCompletionRate, indicators.totalFormAttempts),
    engagementScore: calculateEngagementScore(indicators.totalPageViews, indicators.averageSessionDuration),
    conversionScore: calculateConversionScore(indicators.formCompletionRate),
    sessionHealthScore: calculateSessionHealthScore(indicators.totalSessions, indicators.averageSessionDuration),
  };
}
